import { calcBaseBS } from "../constants/game.constants"
import { BattleCardView, GameCard } from "../types/battle"
import { getInstalledShardSlots } from "@/components/GuardianCard/cardShardLayout"
import { normalizeGameCard } from "./gameCards"

function pickNumber(value: unknown, fallback?: number): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback ?? null
}

export function gameCardToBattleCardView(card: GameCard): BattleCardView {
  return {
    id: card.id,
    element: card.element,
    style: card.style,
    level: card.level,
    colorRank: card.colorRank,
    basePower: card.basePower,
    isReborn: card.isReborn,
    baseBS: calcBaseBS(card),
    victories: card.victories,
    totalBattles: card.totalBattles,
    shardSlots: card.shardSlots,
  }
}

/** Если на карте нет установленных осколков — подтянуть из «руки» / коллекции. */
export function mergeCardShardSlots<T extends { id: string; shardSlots?: GameCard["shardSlots"] }>(
  card: T,
  fromHand?: { id: string; shardSlots?: GameCard["shardSlots"] } | null
): T {
  if (!fromHand || fromHand.id !== card.id) return card
  const installed = getInstalledShardSlots(card.shardSlots ?? null)
  if (installed.length > 0) return card
  const handInstalled = getInstalledShardSlots(fromHand.shardSlots ?? null)
  if (handInstalled.length === 0) return card
  return { ...card, shardSlots: fromHand.shardSlots }
}

type ShardSlotsSource = { id: string; shardSlots?: GameCard["shardSlots"] }

/** Подтянуть осколки с карты из коллекции / колоды, если в payload их нет. */
export function enrichBattleCardShardsFromSource(
  card: BattleCardView,
  sourceCards?: ShardSlotsSource[] | null
): BattleCardView {
  if (!sourceCards?.length) return card
  const from = sourceCards.find((item) => item.id === card.id)
  return mergeCardShardSlots(card, from ?? null)
}

export function enrichBattleCardsShardsFromSource(
  cards: BattleCardView[],
  sourceCards?: ShardSlotsSource[] | null
): BattleCardView[] {
  if (!sourceCards?.length) return cards
  return cards.map((card) => enrichBattleCardShardsFromSource(card, sourceCards))
}

/** Карта из WS / API боя — с осколками для отображения на арене. */
export function normalizeBattleCardView(raw: unknown): BattleCardView | null {
  const gameCard = normalizeGameCard(raw)
  if (!gameCard) return null
  const view = gameCardToBattleCardView(gameCard)
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return view
  const obj = raw as Record<string, unknown>
  const baseBS = pickNumber(obj.baseBS ?? obj.base_bs)
  return baseBS != null ? { ...view, baseBS } : view
}
